import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useNavigate } from "@tanstack/react-router";
import { CommonInstallflowFrame } from "./common-install-flow-frame";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Spinner } from "../ui/spinner";
import { apiClient } from "@/api/client";
import { LocationData } from "@/location/location";
import {
  installFlowOrganizationId,
  installFlowAddress,
  installFlowLocationId,
  installFlowLocationName,
} from "@/installFlow/storage-keys";
const getInstallFlowOrgId = (): number =>
  Number(installFlowOrganizationId.get());

const formSchema = z.object({
  name: z.string().min(1, {
    message: "Please enter a location name"
  })
});

interface AuthProps extends React.HTMLAttributes<HTMLDivElement> {}

export function CreateAssetLocation({ className }: AuthProps) {
  const navigate = useNavigate();
  const orgId = getInstallFlowOrgId();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [apiError, setApiError] = React.useState<string | null>(null);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    setApiError(null);
    try {
      // Read address saved by add-new-location.tsx
      const storedAddress = installFlowAddress.get();
      const address = storedAddress ? JSON.parse(storedAddress) : {};

      // Build payload matching CreateLocationInput (LocationStep.tsx lines 251-255)
      const res = await apiClient.post<LocationData>(
        `/organizations/${orgId}/locations/`,
        {
          name: values.name,
          organization: orgId,
          address_line_1: address.streetAddress ?? "",
          address_line_2: address.aptSuiteUnit ?? "",
          city: address.city ?? "",
          state: address.state ?? "",
          postal_code: address.zipCode ?? "",
          country: "USA",
        },
      );

      // Persist the new location for downstream steps
      installFlowLocationId.set(String(res.data.id));
      installFlowLocationName.set(res.data.name);

      await navigate({ to: "/installFlow/on-site-location" });
    } catch {
      setApiError("Failed to create location. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <CommonInstallflowFrame>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col w-full min-h-[calc(100vh-8rem)]">
          <div className="flex flex-col gap-2 flex-1 pt-10">
            <h1 className="text-xl font-bold text-black leading-[26px]">
              Give this location a name
            </h1>
            <p className="text-base text-[#727272] leading-[20px] font-semibold">
              This will help your team find locations more easily in Thalo.
            </p>
            <div className="mt-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder={'ex: "David\'s House"'}
                        className="rounded-xl border-border"
                        autoFocus
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          {apiError && (
            <p className="text-sm font-medium text-red-600 pb-2" role="alert">
              {apiError}
            </p>
          )}
          <div className="pt-8 pb-2 w-full">
            <Button
              type="submit"
              variant="installFlow"
              disabled={!form.formState.isValid || isSubmitting}
              className="w-full h-[50px] p-4 rounded-[25px]"
            >
              {isSubmitting ? <Spinner className="h-6 w-6" /> : "Continue"}
            </Button>
          </div>
        </form>
      </Form>
    </CommonInstallflowFrame>
  );
}
